/*
 * Purpose: Admin route guard — owner/admin or site admin only
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { Link, Outlet } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { canAccessAdmin } from '@/lib/adminAccess';
import { ContextualError } from '@/components/errors/ContextualError';
import { Button } from '@/components/ui/button';
import { APP_ROUTES } from '@/lib/appRoutes';

export function AdminRouteGuard() {
  const ready = useAuthStore((s) => s.ready);
  const authed = useAuthStore((s) => s.authed);
  const email = useAuthStore((s) => s.email);
  const activeTenant = useAuthStore((s) => s.activeTenant);

  if (!ready) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-muted-foreground text-sm">
        Checking session…
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <ContextualError
          message="Administrator section requires sign-in."
          action={{ label: 'Sign in', href: `${APP_ROUTES.authSignIn}?next=/admin` }}
          secondaryAction={{ label: 'Home', href: APP_ROUTES.home }}
        />
      </div>
    );
  }

  if (!canAccessAdmin({ email, activeTenant })) {
    return (
      <div className="max-w-lg mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2 text-foreground">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          <h1 className="text-lg font-semibold">Administrator access required</h1>
        </div>
        <ContextualError
          message={`Your workspace role (${activeTenant?.role ?? 'member'}) cannot open Admin. Switch to an owner/admin workspace or ask a site administrator to add your email to ADMIN_EMAILS.`}
          action={{ label: 'Open dashboard', href: APP_ROUTES.dashboard }}
          secondaryAction={{ label: 'Support', href: APP_ROUTES.help }}
        />
        <Button variant="outline" size="sm" asChild>
          <Link to={APP_ROUTES.dashboard}>Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return <Outlet />;
}

export default AdminRouteGuard;
